import { useState } from 'react';
import { Banknote, Pencil, Grid3x3 } from 'lucide-react';
import SheetModal, { SheetTlacitko } from '../../components/SheetModal';
import DenominationCounter from '../../components/ui/DenominationCounter';
import { getTextColors } from './sheetStyle';

const fmtKc = (n) => (n == null || isNaN(n)
    ? '—'
    : Math.round(Number(n)).toLocaleString('cs-CZ') + ' Kč');

/**
 * Napočítání peněženky — vlastní modál nad stránkou uzávěrky.
 *
 * Nominály bývaly přímo na stránce a jedenáct dlaždic z ní ukrajovalo skoro
 * polovinu, i když se používá pět a jen jednou za směnu. Napočítání je vlastní
 * úkon: otevře se, dopočítá, zavře a zapíše součet.
 *
 * **Základ se kontroluje.** V peněžence je od rána počáteční částka — když
 * napočítaná hotovost nedosáhne ani na ni, něco chybí dřív, než se začne
 * počítat tržba. Modál to řekne rovnou a nepustí ven; dřív takový stav prošel
 * (nula se tvářila jako platný výsledek).
 *
 * **Nic se nesmí hýbat.** Člověk kouká do peněz a klepe naslepo. Hláška bývala
 * blok nad tlačítky a první klepnutí ji odstranilo, takže dlaždice uskočila
 * pod prstem. Teď je to pevný řádek pod součtem a tlačítka stojí v patičce.
 *
 * @param {number} zaklad - spodní hranice součtu: ranní základ, u rozvozce
 *        ponížený o nákupy zaplacené z tašky; 0 = žádný spodní limit (hlavní
 *        pokladna nemá „ranní základ", má očekávaný stav)
 * @param {string} [popisZakladu] - co stojí v podnadpisu místo „Základ ráno X"
 * @param {number} [ocekavano] - kolik by v ní podle systému mělo být (základ + tržba)
 * @param {string} [nadpis]
 * @param {string} [radekText] - stálý text pod součtem, když se nic neočekává
 * @param {Object} denom - mapa nominál → počet
 * @param {number|null} manualE - null = počítá se z nominálů, číslo = zadáno ručně
 * @param {(zmena: {denom?, manualE?}) => void} onZmena
 * @param {() => void} onHotovo
 */
export default function PenezenkaModal({
    zaklad = 0,
    popisZakladu = null,
    ocekavano = null,
    nadpis = 'Peněženka',
    radekText = null,
    denom = {},
    manualE = null,
    onZmena,
    onHotovo,
    onClose,
    isDark,
    isMobile,
}) {
    const barvy = getTextColors(isDark);
    const [rucne, setRucne] = useState(manualE !== null && manualE !== undefined);

    const soucet = (manualE !== null && manualE !== undefined)
        ? Number(manualE) || 0
        : Object.entries(denom || {}).reduce((s, [n, k]) => s + Number(n) * Number(k || 0), 0);

    const podZakladem = soucet < zaklad;
    const nic = soucet <= 0;

    const prepniRucne = () => {
        const na = !rucne;
        setRucne(na);
        // Přepnutí režimu musí přenést i hodnotu, jinak by se součet vynuloval
        // a člověk by počítal znovu.
        onZmena(na ? { manualE: soucet || 0 } : { manualE: null });
    };

    return (
        <SheetModal
            onClose={onClose}
            isDark={isDark}
            ikona={Banknote}
            barva="#FF8A00"
            nadpis={nadpis}
            podnadpis={popisZakladu ?? `Základ ráno ${fmtKc(zaklad)}`}
            maxSirka={isMobile ? '100%' : 460}
            zIndex={1080}
            paticka={(
                <div style={{ display: 'flex', gap: 8 }}>
                    <SheetTlacitko isDark={isDark} onClick={prepniRucne}>
                        {rucne ? <Grid3x3 size={15} /> : <Pencil size={15} />}
                        {rucne ? 'Po nominálech' : 'Zadat ručně'}
                    </SheetTlacitko>
                    <SheetTlacitko isDark={isDark} hlavni disabled={nic || podZakladem} onClick={onHotovo}>
                        Hotovo · {fmtKc(soucet)}
                    </SheetTlacitko>
                </div>
            )}
        >
            <DenominationCounter
                denominace={denom}
                onChange={(novy) => onZmena({ denom: novy })}
                manualAmount={rucne ? (manualE ?? soucet) : null}
                onManualAmountChange={(v) => onZmena({ manualE: v })}
                ocekavanaCastka={ocekavano}
                radekText={!nic && podZakladem ? `V peněžence má být aspoň ${fmtKc(zaklad)}` : radekText}
                bezPrepinace
                isDark={isDark}
            />
        </SheetModal>
    );
}
